import axios from 'axios';
import uniqid from 'uniqid';
import { jwtDecode } from "jwt-decode";
import { Link, useLocation } from 'react-router-dom';
import { useNavigate } from "react-router-dom";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // Estilos Quill
import React, { useState, useEffect, useRef, useMemo } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Estilos de react-toastify
import BASE_URL from '../../config/config';
import Verificacion from '../../components/Verificacion';
import Suspendido from '../../components/Suspendido';
import Loader from '../../components/Loader';

const NuevoTicket = () => {  
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cargandoHorario, setCargandoHorario] = useState(true);
  const [soporteDisponible, setSoporteDisponible] = useState(false);

  useEffect(() => {
    const calcularDisponibilidad = async () => {
      try {
        const resp = await axios.get(`${BASE_URL}/ajustes/horario`);
        const horario = resp.data?.ajuste?.valor;

        const ahora = new Date();
        const dia = ahora.getDay(); // 0=Dom … 6=Sáb
        const hora = ahora.getHours();
        const minutos = ahora.getMinutes();
        const horaDecimal = hora + minutos / 60;

        // Hora de inicio del sistema (fija: 8:00)
        const horaInicio = horario?.hora_inicio ?? 8;

        // Hora de cierre general y viernes desde ajustes
        const horaCierre = horario?.hora_cierre ?? 16;
        const horaCierreViernes = horario?.hora_cierre_viernes ?? 15;

        const esLaborable = dia >= 1 && dia <= 5; // Lun–Vie
        const esViernes = dia === 5;
        const cierreHoy = esViernes ? horaCierreViernes : horaCierre;

        setSoporteDisponible(esLaborable && horaDecimal >= horaInicio && horaDecimal < cierreHoy);
      } catch {
        // Si falla la petición, calcular con valores por defecto
        const ahora = new Date();
        const dia = ahora.getDay();
        const hora = ahora.getHours();
        const esLaborable = dia >= 1 && dia <= 5;
        const cierreHoy = dia === 5 ? 15 : 16;
        setSoporteDisponible(esLaborable && hora >= 8 && hora < cierreHoy);
      } finally {
        setCargandoHorario(false);
      }
    };

    calcularDisponibilidad();
  }, []);

  const navigate = useNavigate();
  const [content, setContent] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [asunto, setAsunto] = useState('');
  const location = useLocation();
  const departmentFromState = location.state?.department || 'no_especificado'; // Recibir el departamento

  const [departamento, setDepartamento] = useState(departmentFromState); // Inicializar con el valor recibido
  
  const [prioridad, setPrioridad] = useState('no_urgente');
  const [todosUsuarios, setTodosUsuarios] = useState([]);
  const [usuarioDestinatario, setUsuarioDestinatario] = useState(null); // usuario al que se le abre el ticket
  const [busquedaDestinatario, setBusquedaDestinatario] = useState('');
  const [mostrarDropdownDestinatario, setMostrarDropdownDestinatario] = useState(false);
  const dropdownAsignacionRef = useRef(null);

  // Definición de tokenInfo
  const token = localStorage.getItem("AuthToken");
  let tokenInfo = {};
  if (token) {
    tokenInfo = jwtDecode(token);
  }

  // Cargar lista de usuarios (rol user) si el rol es admin o técnico
  useEffect(() => {
    if (tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico') {
      axios.get(`${BASE_URL}/todos-usuarios`)
        .then(res => setTodosUsuarios((res.data.usuarios || []).filter(u => u.role === 'user')))
        .catch(err => console.error('Error al cargar usuarios:', err));
    }
  }, [tokenInfo.role]);

  // Cerrar dropdown al hacer click fuera
  useEffect(() => {
    const handleClickFuera = (e) => {
      if (dropdownAsignacionRef.current && !dropdownAsignacionRef.current.contains(e.target)) {
        setMostrarDropdownDestinatario(false);
      }
    };
    document.addEventListener('mousedown', handleClickFuera);
    return () => document.removeEventListener('mousedown', handleClickFuera);
  }, [])

  // Usuarios filtrados por búsqueda (solo rol user)
  const usuariosFiltrados = useMemo(() =>
    todosUsuarios.filter(u =>
      `${u.nombre} ${u.apellidos} ${u.usuario}`.toLowerCase().includes(busquedaDestinatario.toLowerCase())
    ), [todosUsuarios, busquedaDestinatario]);

  const seleccionarDestinatario = (usuario) => {
    setUsuarioDestinatario(usuario);
    setBusquedaDestinatario('');
    setMostrarDropdownDestinatario(false);
  };

  const limpiarDestinatario = () => {
    setUsuarioDestinatario(null);
    setBusquedaDestinatario('');
  };

  if (tokenInfo.estado === 'verificar') return <Verificacion />;
  if (tokenInfo.estado === 'suspendido') return <Suspendido />;
  if (cargandoHorario) return <div className="loader-fullscreen"><Loader /></div>;

  const handleChange = (value) => {
    setContent(value);
  };

  const handleAttachmentChange = (e) => {
    const files = e.target.files;
    const maxFileSize = 5 * 1024 * 1024; // 5MB en bytes
    const validFiles = [];
  
    for (let i = 0; i < files.length; i++) {
      if (files[i].size <= maxFileSize) {
        validFiles.push(files[i]);
      } else {
        toast.error(`El archivo ${files[i].name} excede el tamaño máximo de 5MB.`);
      }
    }
  
    setAttachments(validFiles);
  };
  

  const handleRemoveAttachment = (index) => {
    const updatedAttachments = [...attachments];
    updatedAttachments.splice(index, 1);
    setAttachments(updatedAttachments);
  };

  const handleAsuntoChange = (e) => {
    setAsunto(e.target.value);
  };

  const handleDepartamentoChange = (e) => {
    setDepartamento(e.target.value);
  };
  const handlePrioridadChange = (e) => {
    setPrioridad(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    window.scrollTo({ top: 0, behavior: 'smooth' });

    if (asunto.trim().length < 5) {
        toast.error('El asunto no puede estar vacío y debe tener al menos 5 caracteres.');
        return;
    }

    if (content.trim().length < 5) {
        toast.error('La descripción no puede estar vacía y debe tener al menos 5 caracteres.');
        return;
    }

    setIsSubmitting(true);
      // Validación de selección de departamento
      if (departamento === 'no_especificado') {
        toast.error('Debes seleccionar un departamento.');
        setIsSubmitting(false);
        return;
      }

    const identificador_unico = uniqid.time();
    const uniqid_up = identificador_unico.toUpperCase();

    // Si se abre el ticket en nombre de otro usuario, usar sus datos
    const creador = usuarioDestinatario || tokenInfo;

    const formData = new FormData();
    formData.append('identificador', uniqid_up);
    formData.append('nombre', creador.nombre);
    formData.append('apellidos', creador.apellidos);
    formData.append('telefono', creador.telefono);
    formData.append('email', creador.email);
    formData.append('webs_del_cliente', creador.webs_del_cliente);
    formData.append('asunto', asunto);
    formData.append('descripcion', content);
    formData.append('dpto', departamento);
    formData.append('usuario_creador', creador.usuario);
    formData.append('abierto_por', tokenInfo.usuario);
    formData.append('prioridad', prioridad);

    for (let i = 0; i < attachments.length; i++) {
        formData.append('archivos', attachments[i]);
    }

    try {
        // Crear el ticket
        const response = await axios.post(`${BASE_URL}/crear-ticket`, formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            }
        });
        window.scrollTo({ top: 0, behavior: 'smooth' });

        navigate(`/ticket-creado/${response.data.identificador}`); 

        // Llamar a la nueva ruta para notificar a administrador
        await axios.post(`${BASE_URL}/notificar-nuevo-ticket-admin`, {
            asunto: asunto,
            identificador: uniqid_up,
            usuario_creador: creador.usuario,
            descripcion: content,
            nombre: creador.nombre,
            apellidos: creador.apellidos,
            dpto: departamento,
            // Si es un técnico abriendo en nombre de un usuario, se indica para usar plantilla distinta
            ...(usuarioDestinatario && {
                nombre_tecnico: tokenInfo.nombre,
                usuario_tecnico: tokenInfo.usuario
            })
        });

        await axios.post(`${BASE_URL}/notificar-nuevo-ticket-cliente`, {
          asunto: asunto,
          identificador: uniqid_up,
          usuario_creador: creador.usuario,
          descripcion: content,
          email_cliente: creador.email,
          nombre_cliente: creador.nombre
      });

        // Si el ticket fue abierto en nombre de un usuario por un técnico/admin, notificar al destinatario
        if (usuarioDestinatario) {
          await axios.post(`${BASE_URL}/notificar-ticket-gestion`, {
            identificador: uniqid_up,
            asunto: asunto,
            descripcion: content,
            dpto: departamento,
            nombre_destinatario: usuarioDestinatario.nombre,
            email_destinatario: usuarioDestinatario.email,
            nombre_tecnico: tokenInfo.nombre,
            usuario_tecnico: tokenInfo.usuario
          });
        }

    } catch (error) {
        toast.error('Error al crear el ticket. Inténtalo de nuevo más tarde.');
        console.error('Error al crear el ticket:', error);
        setIsSubmitting(false); // Desbloquear el botón si hay error
    }
  };


  return (
    <main className='nuevo-ticket site-width'>
      <div className="bloque-ticket">
        <div className="texto-ticket">
          <div className="intro-ticket">
            <h2>👋🏻 ¡Hola {tokenInfo.nombre}!</h2>
            {(tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico') ? (
              <p>Estás creando un ticket como <strong>{tokenInfo.role === 'admin' ? 'administrador' : 'técnico'}</strong>. Puedes asignarlo directamente a cualquier usuario o técnico.</p>
            ) : (
              <>
                <p>¿Tienes algún problema o simplemente tarea nueva a realizar?</p>
                <p>Rellena el siguiente formulario y te asignaremos a un técnico para que gestione tu tarea/incidencia lo antes posible a través de un chat en tiempo real.</p>
              </>
            )}
          </div>
          <div className="datos-preticket">
            <p>¿Son correctos tus datos?<Link to={"/dashboard"}>Editar</Link></p>
            <p className='parrafo-preticket'>
              {(tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico')
                ? 'El ticket quedará registrado con tu cuenta. Asegúrate de rellenar todos los campos correctamente.'
                : 'Usaremos la siguiente información para notificarte y contactarte en caso necesario.'}
            </p>
            <div className="contacto-info-bloque">
            <p className='contacto-info'><span>Nombre:</span> {tokenInfo.nombre}</p>
            <p className='contacto-info'><span>Apellidos:</span> {tokenInfo.apellidos}</p>
              <p className='contacto-info'><span>Teléfono:</span> {tokenInfo.telefono}</p>
              <p className='contacto-info'><span>Email:</span> {tokenInfo.email}</p>
              <p className='contacto-info'><span>Sitios en mantenimiento:</span> {tokenInfo.webs_del_cliente}</p>
            </div>
          </div>
        </div>
        <div className="formulario-ticket">
          <h3>
            {(tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico')
              ? 'Crear nuevo ticket'
              : '¡Estás a punto de crear un ticket!'}
          </h3>

          {soporteDisponible ? '' : <div className='adhoc-warning warning-ticket'><p>{(tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico') ? 'Estás fuera de horario laboral. El ticket será gestionado por orden de llegada en el próximo horario hábil.' : 'Nuestros técnicos no están disponibles fuera de horario laboral, al crear un ticket, se responderá dentro de horario por orden de llegada.'}</p></div>} 

          {(tokenInfo.role === 'admin' || tokenInfo.role === 'tecnico') && (
            <div className="bloque-form bloque-form--asignacion">
              <label>Abrir ticket a un usuario <span className='opcional-msg'>(Opcional)</span></label>

              {usuarioDestinatario ? (
                <div className="asignacion-chip">
                  <div className="asignacion-chip-info">
                    <span className="asignacion-chip-nombre">{usuarioDestinatario.nombre} {usuarioDestinatario.apellidos}</span>
                    <span className="asignacion-chip-usuario">@{usuarioDestinatario.usuario}</span>
                    <span className="asignacion-chip-email">{usuarioDestinatario.email}</span>
                  </div>
                  <button type="button" className="asignacion-chip-quitar" onClick={limpiarDestinatario} title="Quitar">&#10005;</button>
                </div>
              ) : (
                <div className="selector-usuario-busqueda" ref={dropdownAsignacionRef}>
                  <div className="campo-busqueda-usuario">
                    <input
                      type="text"
                      className="input-busqueda-usuario"
                      placeholder="Busca por nombre, apellidos o @usuario..."
                      value={busquedaDestinatario}
                      onChange={(e) => setBusquedaDestinatario(e.target.value)}
                      onFocus={() => setMostrarDropdownDestinatario(true)}
                    />
                    {busquedaDestinatario && (
                      <button type="button" className="boton-limpiar-usuario" onClick={() => setBusquedaDestinatario('')}>&#10005;</button>
                    )}
                  </div>

                  {mostrarDropdownDestinatario && (
                    <div className="lista-usuarios-desplegable">
                      {usuariosFiltrados.length > 0 ? (
                        usuariosFiltrados.map(u => (
                          <div key={u.usuario} className="item-usuario-desplegable" onClick={() => seleccionarDestinatario(u)}>
                            <div className="info-usuario-item">
                              <span className="nombre-usuario-item">{u.nombre} {u.apellidos}</span>
                              <span className="usuario-item">@{u.usuario}</span>
                            </div>
                            <span className="email-usuario-item">{u.email}</span>
                          </div>
                        ))
                      ) : (
                        <div className="sin-resultados-usuario">No se encontraron usuarios</div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
          <div className="bloque-form">
            <label>¿Qué departamento se encargará de tu tarea/incidencia? <span className='obligatorio-msg'>*</span></label>
            <select name="opciones" id="opciones" value={departamento} onChange={handleDepartamentoChange}>
              <option value="no_especificado">Seleccione el departamento correspondiente</option>
              <option value="diseno">Diseño Gráfico</option>
              <option value="desarrollo">Desarrollo Web</option>
              <option value="marketing">Marketing</option>
              <option value="redes_sociales">Redes Sociales</option>
            </select>
            {
              departamento === "desarrollo" ? <p className='desc-dpto-desarrollo'>Si tu tarea/incidencia está relacionada con tu página web, software a medida, correo electrónico, dominios y hosting.</p>: ''
            }
            {
              departamento === "diseno" ? <p className='desc-dpto-diseno'>Si tu tarea/incidencia está relacionada con branding, diseño editorial, diseño para eventos, papelería, rotulación, material promocional, producción de video corporativo/publicitario, edición fotográfica/postproducción de video y fotografía corporativa/producto.</p>: ''
            }
            {
              departamento === "marketing" ? <p className='desc-dpto-marketing'>Si tu tarea/incidencia está relacionada con SEO, campañas de pago (SEM + SMM), campañas de email marketing, informes mensuales de analítica web o análisis y diseño de experiencias e interfaces de usuario.</p>: ''
            }
            {
              departamento === "redes_sociales" ? <p className='desc-dpto-redes_sociales'>Si tu tarea/incidencia está relacionada con la dinamización de tus redes sociales.</p>: ''
            }
            
          </div>
          <div className="bloque-form">
            <label>Asunto <span className='obligatorio-msg'>*</span></label>
            <input type="text" placeholder='Introduzca un título a tu tarea/incidencia...' value={asunto} onChange={handleAsuntoChange} />
          </div>
          <div className="bloque-form">
            <label>Describa detalladamente tu tarea/incidencia <span className='obligatorio-msg'>*</span></label>
            <ReactQuill
              theme="snow"
              value={content}
              onChange={handleChange}
            />
          </div>
          <div className="bloque-form">
            <label>Adjuntar archivos <span className='opcional-msg'>(Opcional)</span></label>
            <p>Si no puedes optimizar tus archivos, te sugerimos utilizar <a href='https://wetransfer.com/' target='_blank'>WeTransfer</a> y proporcionarnos el enlace de descarga en la descripción de tu tarea.</p>
            <label htmlFor="file" className="labelFile">
              <p>Arrastra y suelta aquí tus archivos<br/>(.pdf, .doc, .docx, .jpg, .png, .jpeg, .webp, .pages y .html)<br/>Peso máximo por archivo: 5MB</p>
            <input accept=".pdf,.doc,.docx,.jpg,.png,.jpeg,.webp,.pages,.html" name="text" id="file" type="file" multiple onChange={handleAttachmentChange} />
            </label>
            {attachments && attachments.length > 0 && (
                <div className='files-grid'>
                  {Array.from(attachments).map((file, index) => (
                    <div className='file'>
                      <img src="/images/nuevo-documento.svg" alt="Archivo" />
                      <p key={index}>{file.name}</p>
                      <button className='remove-file' onClick={() => handleRemoveAttachment(index)}>x</button>
                    </div>
                  ))}
                </div>
            )}
          </div>
          <div className="bloque-form">
            <label>¿Es urgente?</label>
            <select name="opciones_prioridad" id="opciones_prioridad" value={prioridad} onChange={handlePrioridadChange}>
              <option value="no_urgente">No</option>
              <option value="urgente">Si</option>
            </select>
          </div>
          <button
            className='enviar-ticket'
            onClick={handleSubmit}
            disabled={isSubmitting} // Bloquear el botón
          >
            {isSubmitting ? 'Enviando...' : 'Enviar'}
          </button>        </div>
      </div>
      <ToastContainer position="bottom-right" autoClose={3000} />
    </main>
  )
}

export default NuevoTicket;
